import { Slice, Vector3 } from 'features/morphing/state/parameterAtoms';
import { isNaturalVector } from 'shared/utils/vectorCheck';
import { isValidSlice } from 'shared/utils/sliceCheck';

const getMorphingLength = (
  morphingMin: Vector3,
  morphingMax: Vector3
) => {
  return {
    x: morphingMax.x - morphingMin.x,
    y: morphingMax.y - morphingMin.y,
    z: morphingMax.z - morphingMin.z
  };
};

export const isDrawable = (
  controlPoints: Vector3,
  morphingMin: Vector3,
  morphingMax: Vector3
): boolean => {
  const morphingLength = getMorphingLength(morphingMin, morphingMax);
  if (isNaturalVector(controlPoints) && isNaturalVector(morphingLength)) {
    return true;
  } else {
    return false;
  };
};

export const isSelectable = (
  targetControlPointsX: Slice,
  targetControlPointsY: Slice,
  targetControlPointsZ: Slice,
  controlPoints: Vector3
) => {
  const validX = isValidSlice(targetControlPointsX, controlPoints.x);
  const validY = isValidSlice(targetControlPointsY, controlPoints.y);
  const validZ = isValidSlice(targetControlPointsZ, controlPoints.z);
  return validX && validY && validZ;
};

export const createCoordinates = (
  controlPoints: Vector3,
  morphingMin: Vector3,
  morphingMax: Vector3,
) => {
  const { x: cx, y: cy, z: cz } = controlPoints;
  const { x: ox, y: oy, z: oz } = morphingMin;
  const { x: lx, y: ly, z: lz } = getMorphingLength(morphingMin, morphingMax);

  const xInterval: number = cx > 1 ? lx / (1000 * (cx - 1)) : 0;
  const yInterval: number = cy > 1 ? ly / (1000 * (cy - 1)) : 0;
  const zInterval: number = cz > 1 ? lz / (1000 * (cz - 1)) : 0;

  const xCoords = Array.from({ length: cx }, (_, i) => 0.001 * ox + i * xInterval);
  const yCoords = Array.from({ length: cy }, (_, i) => 0.001 * oy + i * yInterval);
  const zCoords = Array.from({ length: cz }, (_, i) => 0.001 * oz + i * zInterval);

  const coordinates: [number, number, number][] = xCoords.flatMap(x =>
    yCoords.flatMap(y =>
      zCoords.map(z => [x, y, z] as [number, number, number])
    )
  );

  return coordinates;
};

const getSelectedCoordinates = (
  controls: Vector3,
  coordinates: [number, number, number][],
  targetControlPointsX: Slice,
  targetControlPointsY: Slice,
  targetControlPointsZ: Slice,
  ySymmetry: boolean
) => {
  const [xs, xe] = [targetControlPointsX.start, targetControlPointsX.stop];
  const [ys, ye] = [targetControlPointsY.start, targetControlPointsY.stop];
  const [zs, ze] = [targetControlPointsZ.start, targetControlPointsZ.stop];
  const [cy, cz] = [controls.y, controls.z];

  // X, Y, Z の範囲インデックスを作成
  const xIndices = Array.from(
    { length: xe - xs + 1 },
    (_, i) => i + xs - 1
  );
  const yIndices = Array.from(
    { length: ye - ys + 1 },
    (_, i) => i + ys - 1
  );
  const zIndices = Array.from(
    { length: ze - zs + 1 },
    (_, i) => i + zs - 1
  );

  // ySymmetryがtrueの場合にオフセットした範囲を追加
  const symmetricYIndices = ySymmetry
    ? [
      ...yIndices,
      ...Array.from({ length: ye - ys + 1 }, (_, i) => controls.y - ye + i)
    ]
    : yIndices;

  const indices = xIndices.flatMap(xi =>
    symmetricYIndices.flatMap(yi =>
      zIndices.map(zi => cy * cz * xi + cz * yi + zi)
    )
  );

  return indices.map(index => coordinates[index]);
};

export const createSelectCoordinates = (
  controlPoints: Vector3,
  morphingMin: Vector3,
  morphingMax: Vector3,
  targetControlPointsX: Slice,
  targetControlPointsY: Slice,
  targetControlPointsZ: Slice,
  ySymmetry: boolean
) => {
  const coordinates = createCoordinates(controlPoints, morphingMin, morphingMax);
  const selectedCoordinates = getSelectedCoordinates(
    controlPoints,
    coordinates,
    targetControlPointsX,
    targetControlPointsY,
    targetControlPointsZ,
    ySymmetry
  );

  const remainder: [number, number, number][] = coordinates.filter(
    c => !selectedCoordinates.some(sc => JSON.stringify(sc) === JSON.stringify(c))
  );

  return { selectedCoordinates, remainder };
};


